import { useEffect } from 'react'
import { create } from 'zustand'

import { GTM } from '../utils/gtm'
import type { EVENTS } from '../utils/gtm/constants'
import type { TGTMVariables } from '../utils/gtm/types/GTMVariables'

/**
 * @param event - The event to be tracked
 * @param value - The unique value associated with the event
 * @param variables - The variables associated with the event
 */
type TTrackOnce = (
  /** The event to be tracked */
  event: EVENTS,
  /** The unique value associated with the event */
  value: number | string | undefined,
  /** The variables associated with the event */
  variables?: TGTMVariables,
) => void

type TTrackOnceStore = Partial<Record<EVENTS, (number | string)[]>> & {
  trackOnce: TTrackOnce
}

/**
 * Track an event once, based on an unique value associated with the event.
 * This is useful to track events that can happen multiple times, but we only want to track them once.
 *
 * @example
 * ```ts
 * useTrackOnce.trackOnce(EVENTS.COMMUNITY_VIEW, communityId)
 * ```
 */
const useTrackOnce = create<TTrackOnceStore>((set) => ({
  trackOnce: (event, value, variables) =>
    set((state) => {
      if (!value) return state

      const currentValues = state[event] || []
      if (currentValues.includes(value)) return state

      GTM.sendEvent(event, variables)
      return { ...state, [event]: [...currentValues, value] }
    }),
}))

export const useTrackDisplayed: TTrackOnce = (event, value, variables) => {
  const trackOnce = useTrackOnce((state) => state.trackOnce)
  useEffect(() => trackOnce(event, value, variables), [event, value, variables, trackOnce])
}
